import { computed, getCurrentInstance } from 'vue';
import { showErrorMsg } from '@/common/utils/commonUtils';
import { useInternational } from '@/common/locale';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import {
  findPermissionByTranslateId,
  useRolePermission,
} from '@/common/permission/permission.utils';
import { checkRole } from '@/common/auth/auth.utils';
import { useMyInfoStore } from '@/config/stores/my-info';
import { storeToRefs } from 'pinia';

interface DashboardPermissionData {
  isMyDashboard?: boolean;
  isFeatured?: boolean;
  isFeatureReport?: boolean;
  showError?: boolean;
}

type DashboardPermissionCheckFn = (data: DashboardPermissionData) => boolean;

export const convertPermissionToBoolean = (
  value?: 'FALSE' | 'INDETERMINATE' | 'TRUE',
): boolean | 'INDETERMINATE' => {
  if (!value) return false;
  if (value === 'INDETERMINATE') return 'INDETERMINATE';
  return value === 'TRUE';
};

export const deniedToRouteToEditDashboard: DashboardPermissionCheckFn = ({
  isFeatured,
  isMyDashboard,
  isFeatureReport,
}) => {
  if (isMyDashboard) return false;

  // 기본 대시보드, 기본 리포트면 편집 불가능
  if (isFeatured || isFeatureReport) {
    return true;
  }

  // 기본 대시보드, 기본 리포트 둘 다 아닌 경우
  return (
    !isFeatured &&
    !isFeatureReport &&
    !findPermissionByTranslateId(ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_EDIT)?.permission
  );
};

export const useDashboardPermission = () => {
  const ctx = getCurrentInstance()!.appContext.config.globalProperties;
  const { myInfo } = storeToRefs(useMyInfoStore());
  const isMaster = computed(() => {
    const myRole = myInfo.value.role;
    return checkRole.isMaster(myRole);
  });
  const { t } = useInternational();
  const { isPermissionDenied } = useRolePermission();

  const deniedToAddDashboard = ({ showError }: { showError?: boolean }) => {
    return isPermissionDenied({
      type: 'permission',
      showError,
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_EDIT,
    });
  };

  const deniedToDuplicateDashboard = ({
    isMyDashboard,
    showError,
  }: {
    isMyDashboard?: boolean;
    showError?: boolean;
  }) => {
    if (isMyDashboard) return false;

    return isPermissionDenied({
      type: 'permission',
      showError,
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_EDIT,
    });
  };

  const deniedToAddDashboardList = ({ showError }: { showError?: boolean }) => {
    return isPermissionDenied({
      type: 'permission',
      showError,
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_EDIT,
    });
  };

  const deniedToAddGroup = ({ showError }: { showError?: boolean }) => {
    return isPermissionDenied({
      type: 'permission',
      showError,
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_GROUP_EDIT,
    });
  };

  const deniedToMoveDashboard: DashboardPermissionCheckFn = ({ isFeatured }) => {
    const deniedByMovePermission = isPermissionDenied({
      type: 'permission',
      showError: false,
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_MOVE,
    });
    if (deniedByMovePermission) {
      return true;
    }

    // preset(=featured) 대시보드는 list 이동 시 featured list 권한도 함께 필요
    if (isFeatured) {
      return isPermissionDenied({
        type: 'listOnOff',
        showError: false,
        rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD,
      });
    }

    return false;
  };

  const deniedToMoveDashboardList = ({
    hasFeaturedDashboard = false,
  }: {
    hasFeaturedDashboard?: boolean;
  } = {}) => {
    const deniedByMovePermission = isPermissionDenied({
      type: 'permission',
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_MOVE,
    });
    if (deniedByMovePermission) {
      return true;
    }

    if (hasFeaturedDashboard) {
      return isPermissionDenied({
        type: 'listOnOff',
        rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD,
      });
    }

    return false;
  };

  const deniedToDeleteDashboard = ({
    isMyDashboard,
    showError,
  }: {
    isMyDashboard?: boolean;
    showError: boolean;
  }) => {
    if (isMyDashboard) return false;

    return isPermissionDenied({
      type: 'permission',
      showError,
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_EDIT,
    });
  };

  const deniedToExportDashboard: DashboardPermissionCheckFn = ({
    isFeatured,
    isMyDashboard,
    isFeatureReport,
  }) => {
    if (isMyDashboard) return false;

    // 기본 대시보드, 기본 리포트 둘 다 설정돼있을 경우 둘 다 권한이 없어야 true
    if (
      !!isFeatured &&
      !!isFeatureReport &&
      !findPermissionByTranslateId(
        ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD_EXPORT,
      )?.permission &&
      !findPermissionByTranslateId(ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT_EXPORT)
        ?.permission
    ) {
      return true;
    }

    // 기본 대시보드, 기본 리포트 둘 중 하나만 있는 경우
    if (
      !(!!isFeatured && !!isFeatureReport) &&
      ((!!isFeatured &&
        isPermissionDenied({
          type: 'permission',
          showError: false,
          rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD_EXPORT,
        })) ||
        (!!isFeatureReport &&
          isPermissionDenied({
            type: 'permission',
            showError: false,
            rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT_EXPORT,
          })))
    ) {
      return true;
    }

    // 기본 대시보드, 기본 리포트 둘 다 아닌 경우
    return (
      !isFeatured &&
      !isFeatureReport &&
      isPermissionDenied({
        type: 'permission',
        showError: false,
        rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_EXPORT,
      })
    );
  };

  const deniedToShareDashboard = ({ showError }: { showError?: boolean }) => {
    return isPermissionDenied({
      type: 'permission',
      showError,
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_SHARE,
    });
  };

  const deniedToSetReportDashboard: DashboardPermissionCheckFn = ({ isMyDashboard }) => {
    if (isMaster.value) return false;

    if (isMyDashboard) {
      return isPermissionDenied({
        type: 'permission',
        showError: false,
        rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_SET_REPORT,
      });
    }

    return true;
  };

  const deniedToSetupDashboardByMenu: DashboardPermissionCheckFn = ({
    isFeatured,
    isFeatureReport,
  }: DashboardPermissionData) => {
    if (isMaster.value) return false;

    // 기본 대시보드, 기본 리포트 둘 다 설정돼있을 경우 둘 다 권한이 없어야 true
    if (
      !!isFeatured &&
      !!isFeatureReport &&
      !findPermissionByTranslateId(
        ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD_SETUP_DASHBOARD_BY_MENU,
      )?.permission &&
      !findPermissionByTranslateId(
        ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT_SETUP_DASHBOARD_BY_MENU,
      )?.permission
    ) {
      return true;
    }

    // 기본 대시보드, 기본 리포트 둘 중 하나만 있는 경우
    if (
      !(!!isFeatured && !!isFeatureReport) &&
      ((!!isFeatured &&
        isPermissionDenied({
          type: 'permission',
          showError: false,
          rolePermissionKey:
            ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD_SETUP_DASHBOARD_BY_MENU,
        })) ||
        (!!isFeatureReport &&
          isPermissionDenied({
            type: 'permission',
            showError: false,
            rolePermissionKey:
              ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT_SETUP_DASHBOARD_BY_MENU,
          })))
    ) {
      return true;
    }

    return (
      !isFeatured &&
      !isFeatureReport &&
      isPermissionDenied({
        type: 'permission',
        showError: false,
        rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_SETUP_DASHBOARD_BY_MENU,
      })
    );
  };

  const deniedToSetupAnalysisBoardByMenu: DashboardPermissionCheckFn = ({
    isFeatured,
    isFeatureReport,
  }: DashboardPermissionData) => {
    if (isMaster.value) return false;

    // 기본 대시보드, 기본 리포트 둘 다 설정돼있을 경우 둘 다 권한이 없어야 true
    if (
      !!isFeatured &&
      !!isFeatureReport &&
      !findPermissionByTranslateId(
        ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_ANALYSIS_BOARD_SETUP_DASHBOARD_BY_MENU,
      )?.permission &&
      !findPermissionByTranslateId(
        ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT_SETUP_ANALYSIS_BOARD_BY_MENU,
      )?.permission
    ) {
      return true;
    }

    // 기본 대시보드, 기본 리포트 둘 중 하나만 있는 경우
    if (
      !(!!isFeatured && !!isFeatureReport) &&
      ((!!isFeatured &&
        isPermissionDenied({
          type: 'permission',
          showError: false,
          rolePermissionKey:
            ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_ANALYSIS_BOARD_SETUP_DASHBOARD_BY_MENU,
        })) ||
        (!!isFeatureReport &&
          isPermissionDenied({
            type: 'permission',
            showError: false,
            rolePermissionKey:
              ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT_SETUP_ANALYSIS_BOARD_BY_MENU,
          })))
    ) {
      return true;
    }

    return (
      !isFeatured &&
      !isFeatureReport &&
      isPermissionDenied({
        type: 'permission',
        showError: false,
        rolePermissionKey:
          ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_SETUP_ANALYSIS_BOARD_BY_MENU,
      })
    );
  };

  const deniedToImportDashboard = () => {
    return isPermissionDenied({
      type: 'permission',
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_IMPORT,
    });
  };

  const deniedToImportViewpack = () => {
    return isPermissionDenied({
      type: 'permission',
      rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_IMPORT_VIEWPACK,
    });
  };

  const deniedToSetAsStartScreen: DashboardPermissionCheckFn = ({
    isFeatured,
    isMyDashboard,
    isFeatureReport,
  }) => {
    if (isMyDashboard) return false;

    // 기본 대시보드, 기본 리포트 둘 다 설정돼있을 경우 둘 다 권한이 없어야 true
    if (
      !!isFeatured &&
      !!isFeatureReport &&
      !findPermissionByTranslateId(
        ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD_SET_AS_START_SCREEN,
      )?.permission &&
      !findPermissionByTranslateId(
        ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT_SET_AS_START_SCREEN,
      )?.permission
    ) {
      return true;
    }

    return (
      !(!!isFeatured && !!isFeatureReport) &&
      ((!!isFeatured &&
        isPermissionDenied({
          type: 'permission',
          showError: false,
          rolePermissionKey:
            ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD_SET_AS_START_SCREEN,
        })) ||
        (!!isFeatureReport &&
          isPermissionDenied({
            type: 'permission',
            showError: false,
            rolePermissionKey:
              ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT_SET_AS_START_SCREEN,
          })))
    );
  };

  const deniedToEditDashboard: DashboardPermissionCheckFn = ({
    isFeatured,
    isMyDashboard,
    isFeatureReport,
    showError = true,
  }) => {
    if (isMaster.value || isMyDashboard) return false;

    // 기본 대시보드, 기본 리포트면 편집 불가능
    if (isFeatured || isFeatureReport) {
      if (showError) {
        showErrorMsg(ctx, t('MESSAGE.NO_PERMISSION'));
      }
      return true;
    }

    // 기본 대시보드, 기본 리포트 둘 다 아닌 경우
    return (
      !isFeatured &&
      !isFeatureReport &&
      isPermissionDenied({
        type: 'permission',
        showError,
        rolePermissionKey: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_LIST_EDIT,
      })
    );
  };

  return {
    deniedToAddDashboard,
    deniedToDuplicateDashboard,
    deniedToAddDashboardList,
    deniedToAddGroup,
    deniedToMoveDashboard,
    deniedToMoveDashboardList,
    deniedToDeleteDashboard,
    deniedToExportDashboard,
    deniedToShareDashboard,
    deniedToSetReportDashboard,
    deniedToSetupDashboardByMenu,
    deniedToSetupAnalysisBoardByMenu,
    deniedToImportDashboard,
    deniedToImportViewpack,
    deniedToSetAsStartScreen,
    deniedToEditDashboard,
  };
};

export const deniedDashboard: DashboardPermissionCheckFn = ({
  isMyDashboard,
  isFeatured,
  isFeatureReport,
}) => {
  if (isMyDashboard) return false;

  // 기본 대시보드, 기본 리포트 둘 다 설정돼있을 경우 둘 다 권한이 없어야 true
  if (
    !!isFeatured &&
    !!isFeatureReport &&
    !findPermissionByTranslateId(ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT)
      ?.listOnOff &&
    !findPermissionByTranslateId(ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD)
      ?.listOnOff
  ) {
    return true;
  }

  return (
    !(!!isFeatured && !!isFeatureReport) &&
    ((!!isFeatured &&
      !findPermissionByTranslateId(ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD)
        ?.listOnOff) ||
      (!!isFeatureReport &&
        !findPermissionByTranslateId(ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_REPORT)
          ?.listOnOff))
  );
};
